/**
 * Challenge lifecycle helpers — client-side staleness for pending challenges.
 *
 * Pending direct challenges carry a server-authoritative ``expiresAt`` (Sprint 11
 * WI-3). Staleness reads that field; it never mutates Firestore and never
 * invents terminal state. When ``expiresAt`` is absent (pre-backfill), a legacy
 * game-join guard remains until the server backfill completes.
 */
import {
  gameStartMs,
  isLiveGameStatus,
  isTerminalGameStatus,
} from './gameLockability';
import { findLiveEventForMatchup } from './challengeWinner';

/** Statuses where a challenge is still awaiting accept/agree and can go stale. */
export const PENDING_CHALLENGE_STATUSES = new Set([
  'PENDING_ACCEPTANCE',
  'PENDING_AGREEMENT',
]);

function expiresAtMs(bet) {
  const val = bet?.expiresAt;
  if (val == null) return null;
  if (typeof val === 'number') return val;
  if (val?.toMillis) return val.toMillis();
  const t = new Date(val).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * Legacy game-join staleness guard — used only when ``expiresAt`` is missing.
 * @private
 */
function isPendingChallengeStaleLegacy(bet, opts = {}) {
  const now = opts.now ?? Date.now();
  const graceMs = opts.graceMs ?? DEFAULT_GRACE_MS;
  const matchup = bet.matchup || {};

  const game = findLiveEventForMatchup(opts.lookupGames || [], matchup);

  const status = game?.status ?? matchup.gameStatus;
  if (status && (isLiveGameStatus(status) || isTerminalGameStatus(status))) {
    return true;
  }

  const start = gameStartMs(game) ?? gameStartMs(matchup.startTime);
  if (start != null && start < now - graceMs) return true;

  return false;
}

/**
 * Structured lifecycle audit log — one entry per state mutation.
 *
 * Launch-time debugging aid so "my challenge disappeared" is always traceable
 * to a single line: who changed it, from what, to what, and why. Console-only
 * and side-effect free; safe to remove after stabilization. Never throws.
 *
 * @param {object} entry
 * @param {string} entry.challengeId
 * @param {string} [entry.oldStatus]
 * @param {string} entry.newStatus
 * @param {string} entry.reason   — e.g. sender_cancelled, recipient_declined,
 *                                   both_agreed, accepted, game_started
 * @param {string} [entry.userId] — actor who triggered the change
 * @param {string} [entry.source] — call site, e.g. App.handleDecline
 */
export function logChallengeLifecycle({
  challengeId,
  oldStatus = null,
  newStatus,
  reason,
  userId = null,
  source = null,
} = {}) {
  try {
    const entry = {
      tag: 'ChallengeLifecycle',
      challengeId: challengeId ?? null,
      oldStatus,
      newStatus: newStatus ?? null,
      reason: reason ?? null,
      userId,
      source,
      timestamp: new Date().toISOString(),
    };
    // eslint-disable-next-line no-console
    console.info('[ChallengeLifecycle]', entry);
    return entry;
  } catch {
    return null;
  }
}

const DEFAULT_GRACE_MS = 5 * 60_000;

/**
 * Decide whether a pending challenge should be hidden because its game can no
 * longer be locked. Fails open (returns false) whenever we lack enough data, so
 * legitimate pending challenges are never hidden by accident.
 *
 * @param {object} bet — challenge record (expects `status` and `matchup`)
 * @param {object} [opts]
 * @param {number} [opts.now]
 * @param {object[]} [opts.lookupGames] — live/cached games for matchup resolution
 * @param {number} [opts.graceMs]
 * @returns {boolean}
 */
export function isPendingChallengeStale(bet, opts = {}) {
  if (!bet || !PENDING_CHALLENGE_STATUSES.has(bet.status)) return false;

  const now = opts.now ?? Date.now();
  const expires = expiresAtMs(bet);
  if (expires != null) {
    return expires <= now;
  }

  return isPendingChallengeStaleLegacy(bet, opts);
}

/**
 * Filter out stale pending challenges from a list.
 * @param {object[]} bets
 * @param {object} [opts] — same shape as isPendingChallengeStale opts
 */
export function dropStalePendingChallenges(bets, opts = {}) {
  if (!Array.isArray(bets)) return [];
  return bets.filter((bet) => !isPendingChallengeStale(bet, opts));
}
